import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../../api-config";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

// Iconos MUI
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import HistoryIcon from "@mui/icons-material/History";
import LockClockIcon from "@mui/icons-material/LockClock";
import ShieldIcon from "@mui/icons-material/Shield";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BoltIcon from "@mui/icons-material/Bolt";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const formatSeconds = (seconds) => {
  if (!seconds || seconds <= 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const formatDate = (isoString) => {
  if (!isoString) return "-";
  try {
    const d = new Date(isoString);
    return d.toLocaleString("es-CL", {
      timeZone: "America/Santiago",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return isoString;
  }
};

const TickControl = () => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  // Countdown timers locales para decremento fluido segundo a segundo
  const [stallRemaining, setStallRemaining] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Formulario Stall
  const [durationMinutes, setDurationMinutes] = useState(5);
  const [stallReason, setStallReason] = useState("");

  const token = localStorage.getItem("token");

  const getAuthHeaders = () => {
    const currentToken = localStorage.getItem("token") || token;
    return currentToken
      ? { headers: { Authorization: `Bearer ${currentToken}` } }
      : {};
  };

  // Obtener usuario autenticado
  useEffect(() => {
    if (token) {
      axios
        .get(`${API_URL}/users-api/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data && res.data.email) {
            setUserEmail(res.data.email);
          }
        })
        .catch(() => {});
    }
  }, [token]);

  // Consulta de estado de tick_control
  const fetchStatus = useCallback(async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/usdclp-api/control/status`, getAuthHeaders());
      setStatus(res.data);
      setStallRemaining(res.data.stall_remaining_seconds ?? res.data.seconds_remaining ?? 0);
      setCooldownRemaining(res.data.stall_cooldown_remaining_seconds ?? res.data.cooldown_seconds_remaining ?? 0);
    } catch (err) {
      console.error("Error fetching tick control status:", err);
      if (isInitial) {
        toast.error("Error al sincronizar con el motor de ticks");
      }
    } finally {
      if (isInitial) setLoading(false);
    }
  }, [token]);

  // Historial de auditoría
  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/usdclp-api/control/stall/history?limit=25`, getAuthHeaders());
      setHistory(res.data || []);
    } catch (err) {
      console.error("Error fetching stall history:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus(true);
    fetchHistory();

    const interval = setInterval(() => {
      fetchStatus(false);
    }, 4000);

    return () => clearInterval(interval);
  }, [fetchStatus, fetchHistory]);

  // Decrementador fluido cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setStallRemaining((prev) => (prev > 0 ? prev - 1 : 0));
      setCooldownRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Cambiar modo de precio (mix <-> puntas)
  const handleToggleMode = async (newMode) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/usdclp-api/control/mode`,
        {
          price_mode: newMode,
          mode: newMode,
          updated_by: userEmail || "web_operator",
        },
        getAuthHeaders()
      );
      setStatus(res.data);
      toast.success(`Modo de cotización cambiado a: ${newMode.toUpperCase()}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "No se pudo cambiar el modo";
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  // Activar stall con 1 solo clic (sin modal)
  const handleActivateStall = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/usdclp-api/control/stall/activate`,
        {
          duration_minutes: durationMinutes,
          reason: stallReason.trim() || "Pase a deal manual",
          confirmation_code: "CONFIRMAR",
          requested_by: userEmail || "web_operator",
        },
        getAuthHeaders()
      );
      setStatus(res.data);
      setStallRemaining(res.data.stall_remaining_seconds || durationMinutes * 60);
      setCooldownRemaining(res.data.stall_cooldown_remaining_seconds || 0);
      setStallReason("");
      toast.error(`⚠️ STALL ACTIVADO (${durationMinutes} min). Envío automático detenido en OneZeroConn.`, {
        autoClose: 5000,
      });
      fetchHistory();
    } catch (err) {
      const detail = err.response?.data?.detail || "Error al activar el stall";
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  // Cancelar stall activo
  const handleCancelStall = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/usdclp-api/control/stall/cancel`,
        {
          cancelled_by: userEmail || "web_operator",
          reason: "Cancelación manual desde Frontend",
        },
        getAuthHeaders()
      );
      setStatus(res.data);
      setStallRemaining(0);
      setCooldownRemaining(res.data.stall_cooldown_remaining_seconds || 0);
      toast.success("✅ Stall cancelado. Envío automático reanudado exitosamente.");
      fetchHistory();
    } catch (err) {
      const detail = err.response?.data?.detail || "Error al cancelar el stall";
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  const isStalled = Boolean(status?.stall_active || status?.is_stalled || stallRemaining > 0);
  const inCooldown = !isStalled && Boolean((status?.stall_cooldown_remaining_seconds > 0) || (status?.cooldown_seconds_remaining > 0) || cooldownRemaining > 0);
  const currentMode = status?.price_mode || "mix";

  return (
    <div className="min-h-full p-4 md:p-8 bg-[#0a0e17] text-slate-100 font-sans selection:bg-cyan-500 selection:text-black">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* ========================================================================= */}
        {/* TOP COMMAND DECK / HEADER                                                 */}
        {/* ========================================================================= */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#111827]/90 via-[#151f33]/90 to-[#0f172a]/90 border border-slate-800/80 p-6 shadow-2xl backdrop-blur-xl">
          {/* Ambient light glow */}
          <div className="absolute top-0 right-0 -mt-8 -mr-8 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-1/3 -mb-10 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold tracking-widest uppercase bg-cyan-950/80 text-cyan-400 border border-cyan-800/60 shadow-[0_0_10px_rgba(6,182,212,0.2)] flex items-center gap-1.5">
                  <BoltIcon sx={{ fontSize: 13 }} />
                  ONEZEROCONN ENGINE
                </span>
                <span className="text-xs font-mono text-slate-500">v2.4.0-PROD</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-white flex items-center gap-2">
                USD/CLP Tick Control Deck
              </h1>
              <p className="text-sm text-slate-400 mt-0.5">
                Consola institucional de conmutación algorítmica y stall de emergencia para negociación manual
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Telemetría Operador */}
              <div className="hidden sm:flex items-center gap-2.5 px-4 py-2 rounded-xl bg-slate-900/80 border border-slate-800 text-xs font-mono">
                <ShieldIcon sx={{ fontSize: 16 }} className="text-cyan-400" />
                <div>
                  <div className="text-[10px] text-slate-500 tracking-wider uppercase">OPERADOR ACTIVO</div>
                  <div className="text-slate-200 font-semibold max-w-[170px] truncate">{userEmail || "Operador QRT"}</div>
                </div>
              </div>

              {/* Botón de Sincronización Manual */}
              <button
                onClick={() => {
                  fetchStatus(true);
                  fetchHistory();
                }}
                disabled={loading || actionLoading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/70 text-slate-200 text-xs font-bold tracking-wide transition-all shadow-md active:scale-95 disabled:opacity-50"
              >
                <RefreshIcon className={loading ? "animate-spin" : ""} sx={{ fontSize: 16 }} />
                <span>{loading ? "SYNCING..." : "ACTUALIZAR"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* CRITICAL WARNING HUD (SOLO VISIBLE SI STALL ESTÁ ACTIVO)                   */}
        {/* ========================================================================= */}
        <AnimatePresence>
          {isStalled && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.98 }}
              className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-rose-950/70 via-red-900/40 to-rose-950/70 border-2 border-rose-500/80 p-6 shadow-[0_0_40px_rgba(225,29,72,0.3)] backdrop-blur-xl"
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="relative flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-500/20 border border-rose-500/50 text-rose-400 shadow-[0_0_20px_rgba(244,63,94,0.4)]">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-2xl bg-rose-400 opacity-30" />
                    <WarningAmberIcon sx={{ fontSize: 32 }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-[11px] font-mono font-black uppercase bg-rose-500 text-white tracking-widest">
                        STALL ACTIVO
                      </span>
                      <span className="text-xs font-mono text-rose-300 font-semibold">
                        ORDEN MANUAL HABILITADA
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white mt-1">
                      El envío de ticks automáticos en OneZeroConn está detenido
                    </h3>
                    <p className="text-xs text-rose-200/80">
                      Motivo: <span className="font-semibold text-white">&quot;{status?.stall_requested_by || "Pase a deal manual"}&quot;</span> • La cotización se reanudará automáticamente al expirar el tiempo.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
                  {/* Cronómetro Digital Monospace */}
                  <div className="text-right">
                    <div className="text-[10px] font-mono tracking-widest text-rose-300/80 uppercase">TIEMPO RESTANTE</div>
                    <div className="text-4xl font-mono font-black tracking-wider text-white drop-shadow-[0_0_15px_rgba(244,63,94,0.7)]">
                      {formatSeconds(stallRemaining)}
                    </div>
                  </div>

                  {/* Botón Reanudar Inmediato */}
                  <button
                    onClick={handleCancelStall}
                    disabled={actionLoading}
                    className="flex items-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-black text-sm tracking-wider uppercase shadow-[0_0_25px_rgba(16,185,129,0.4)] hover:shadow-[0_0_35px_rgba(16,185,129,0.6)] transition-all active:scale-95 disabled:opacity-50"
                  >
                    <PlayArrowIcon />
                    <span>REANUDAR AHORA</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ========================================================================= */}
        {/* TELEMETRY KPI CARDS                                                       */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* Card 1: Modo de Cotización */}
          <div className="relative overflow-hidden rounded-xl bg-[#111827]/80 border border-slate-800/90 p-5 shadow-lg backdrop-blur-md">
            <div className="flex items-center justify-between text-xs font-mono text-slate-400 uppercase tracking-wider mb-2">
              <span>MODO DE PRECIO</span>
              <SpeedIcon sx={{ fontSize: 18 }} className="text-cyan-400" />
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <span className={`text-2xl font-black tracking-tight font-mono ${currentMode === "mix" ? "text-cyan-400" : "text-emerald-400"}`}>
                {currentMode.toUpperCase()}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold tracking-wider uppercase border ${
                currentMode === "mix"
                  ? "bg-cyan-950/80 text-cyan-300 border-cyan-800/80"
                  : "bg-emerald-950/80 text-emerald-300 border-emerald-800/80"
              }`}>
                {currentMode === "mix" ? "PONDERADO" : "PUNTAS DIR."}
              </span>
            </div>
            <div className="text-[11px] text-slate-400 mt-2">
              {currentMode === "mix" ? "Algoritmo de media y spread ponderado" : "Transmisión directa de puntas bid/ask"}
            </div>
          </div>

          {/* Card 2: Estado del Feed OneZero */}
          <div className={`relative overflow-hidden rounded-xl bg-[#111827]/80 border p-5 shadow-lg backdrop-blur-md transition-colors ${
            isStalled ? "border-rose-500/60 shadow-[0_0_20px_rgba(244,63,94,0.15)]" : "border-slate-800/90"
          }`}>
            <div className="flex items-center justify-between text-xs font-mono text-slate-400 uppercase tracking-wider mb-2">
              <span>ESTADO DE TRANSMISIÓN</span>
              <div className="relative flex h-2.5 w-2.5">
                {isStalled ? (
                  <>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
                  </>
                ) : (
                  <>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                  </>
                )}
              </div>
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <span className={`text-2xl font-black tracking-tight font-mono ${isStalled ? "text-rose-400" : "text-emerald-400"}`}>
                {isStalled ? "STALL (DETENIDO)" : "TRANSMITIENDO"}
              </span>
            </div>
            <div className="text-[11px] text-slate-400 mt-2">
              {isStalled ? `Reanudación en ${formatSeconds(stallRemaining)}` : "Ticks activos hacia OneZeroConn en vivo"}
            </div>
          </div>

          {/* Card 3: Cooldown Anti-Spam */}
          <div className="relative overflow-hidden rounded-xl bg-[#111827]/80 border border-slate-800/90 p-5 shadow-lg backdrop-blur-md">
            <div className="flex items-center justify-between text-xs font-mono text-slate-400 uppercase tracking-wider mb-2">
              <span>COOLDOWN ANTI-SPAM (15M)</span>
              <LockClockIcon sx={{ fontSize: 18 }} className={inCooldown ? "text-amber-400" : "text-slate-500"} />
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <span className={`text-2xl font-black tracking-tight font-mono ${inCooldown ? "text-amber-400" : "text-slate-200"}`}>
                {inCooldown ? formatSeconds(cooldownRemaining) : "LISTO"}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold tracking-wider uppercase border ${
                inCooldown
                  ? "bg-amber-950/80 text-amber-300 border-amber-800/80"
                  : "bg-slate-800 text-slate-300 border-slate-700"
              }`}>
                {inCooldown ? "BLOQUEADO" : "DESPEJADO"}
              </span>
            </div>
            <div className="text-[11px] text-slate-400 mt-2">
              {inCooldown ? "Espera de seguridad obligatoria entre stalls" : "Ventana disponible para iniciar stall si es necesario"}
            </div>
          </div>

          {/* Card 4: Cobertura y Sincronía */}
          <div className="relative overflow-hidden rounded-xl bg-[#111827]/80 border border-slate-800/90 p-5 shadow-lg backdrop-blur-md">
            <div className="flex items-center justify-between text-xs font-mono text-slate-400 uppercase tracking-wider mb-2">
              <span>LATENCIA Y CONTROL</span>
              <AccessTimeIcon sx={{ fontSize: 18 }} className="text-blue-400" />
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-2xl font-black tracking-tight font-mono text-blue-400">
                &lt; 50 ms
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold tracking-wider uppercase bg-blue-950/80 text-blue-300 border border-blue-800/80">
                TIEMPO REAL
              </span>
            </div>
            <div className="text-[11px] text-slate-400 mt-2">
              Actualización síncrona vía WebSocket + PostgreSQL
            </div>
          </div>

        </div>

        {/* ========================================================================= */}
        {/* OPERATIONAL INTERACTION DECK (MODO SWITCHER + STALL LAUNCHPAD)           */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* PANEL IZQUIERDO: SELECTOR DE MODO DE PRECIO */}
          <div className="relative rounded-2xl bg-gradient-to-b from-[#111827] to-[#0d131f] border border-slate-800 p-6 shadow-xl flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                    <TuneIcon sx={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white tracking-tight">Modo de Cotización Activo</h2>
                    <p className="text-xs text-slate-400">Selecciona el modelo de cálculo que se transmitirá a la contraparte</p>
                  </div>
                </div>
              </div>

              {/* CYBER-SEGMENTED SWITCHER */}
              <div className="grid grid-cols-2 gap-3 p-1.5 rounded-2xl bg-slate-950/80 border border-slate-800/90 my-6">
                
                {/* OPCION MIX */}
                <button
                  type="button"
                  onClick={() => handleToggleMode("mix")}
                  disabled={actionLoading}
                  className={`relative p-4 rounded-xl text-left transition-all duration-200 ${
                    currentMode === "mix"
                      ? "bg-gradient-to-br from-cyan-950/90 to-blue-950/90 border border-cyan-500/60 shadow-[0_0_20px_rgba(6,182,212,0.25)]"
                      : "bg-transparent border border-transparent hover:bg-slate-900/60 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-base font-black font-mono tracking-wide ${currentMode === "mix" ? "text-cyan-300" : "text-slate-300"}`}>
                      MODO MIX
                    </span>
                    {currentMode === "mix" && (
                      <CheckCircleIcon sx={{ fontSize: 18 }} className="text-cyan-400" />
                    )}
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Algoritmo estándar de <code className="text-cyan-300 font-mono">send_ticks.py</code> con ponderación por volumen de mercado.
                  </p>
                </button>

                {/* OPCION PUNTAS */}
                <button
                  type="button"
                  onClick={() => handleToggleMode("puntas")}
                  disabled={actionLoading}
                  className={`relative p-4 rounded-xl text-left transition-all duration-200 ${
                    currentMode === "puntas"
                      ? "bg-gradient-to-br from-emerald-950/90 to-teal-950/90 border border-emerald-500/60 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
                      : "bg-transparent border border-transparent hover:bg-slate-900/60 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-base font-black font-mono tracking-wide ${currentMode === "puntas" ? "text-emerald-300" : "text-slate-300"}`}>
                      MODO PUNTAS
                    </span>
                    {currentMode === "puntas" && (
                      <CheckCircleIcon sx={{ fontSize: 18 }} className="text-emerald-400" />
                    )}
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Transmisión pura y directa de las puntas operativas más competitivas (Bid/Ask).
                  </p>
                </button>

              </div>
            </div>

            {/* Micro Guía */}
            <div className="p-3.5 rounded-xl bg-slate-900/70 border border-slate-800/80 text-xs text-slate-400 flex items-start gap-2.5">
              <BoltIcon sx={{ fontSize: 16 }} className="text-cyan-400 shrink-0 mt-0.5" />
              <span>
                El cambio de modo toma efecto de forma instantánea en la siguiente iteración de publicación (frecuencia sub-segundo).
              </span>
            </div>
          </div>

          {/* PANEL DERECHO: STALL STATION (PASE A DEALEO MANUAL) */}
          <div className="relative rounded-2xl bg-gradient-to-b from-[#111827] to-[#0d131f] border border-slate-800 p-6 shadow-xl flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-rose-500/10 text-rose-400 border border-rose-500/20">
                    <PauseIcon sx={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white tracking-tight">Stall de Precio (Pase a Manual)</h2>
                    <p className="text-xs text-slate-400">Detiene el streaming para cargar transacciones manualmente sin interferencias</p>
                  </div>
                </div>
              </div>

              {/* SELECTOR DE DURACIÓN (PILLS) */}
              <div className="my-5">
                <label className="block text-xs font-mono font-bold tracking-wider uppercase text-slate-300 mb-2">
                  DURACIÓN DEL STALL (MINUTOS)
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[3, 4, 5].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setDurationMinutes(m)}
                      disabled={isStalled || inCooldown || actionLoading}
                      className={`py-3 px-4 rounded-xl font-mono font-bold text-sm tracking-wide transition-all ${
                        durationMinutes === m
                          ? "bg-rose-500 text-white shadow-[0_0_15px_rgba(244,63,94,0.4)] border border-rose-400"
                          : "bg-slate-900/90 text-slate-400 hover:text-slate-200 border border-slate-800 hover:border-slate-700"
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      {m} MINUTOS
                    </button>
                  ))}
                </div>
              </div>

              {/* CAMPO MOTIVO (OPCIONAL) */}
              <div className="mb-5">
                <label className="block text-xs font-mono font-bold tracking-wider uppercase text-slate-300 mb-2">
                  JUSTIFICACIÓN / REFERENCIA DE DEALEO (OPCIONAL)
                </label>
                <input
                  type="text"
                  value={stallReason}
                  onChange={(e) => setStallReason(e.target.value)}
                  placeholder="Ej: Dealeo manual punta compradora Banco X"
                  disabled={isStalled || inCooldown || actionLoading}
                  className="w-full px-4 py-3 rounded-xl bg-slate-950/90 border border-slate-800 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-all font-sans disabled:opacity-40"
                />
              </div>
            </div>

            {/* BOTÓN DE ACCIÓN PRINCIPAL (1 SOLO CLIC) */}
            <div className="mt-2">
              {isStalled ? (
                <button
                  type="button"
                  onClick={handleCancelStall}
                  disabled={actionLoading}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-sm tracking-wider uppercase shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <PlayArrowIcon />
                  <span>REANUDAR ENVÍO INMEDIATAMENTE</span>
                </button>
              ) : inCooldown ? (
                <div className="p-3.5 rounded-xl bg-amber-950/30 border border-amber-800/60 text-amber-300 text-xs font-mono flex items-center justify-center gap-2.5">
                  <LockClockIcon sx={{ fontSize: 18 }} />
                  <span>EN COOLDOWN ANTI-SPAM: DISPONIBLE EN {formatSeconds(cooldownRemaining)}</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleActivateStall}
                  disabled={actionLoading}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-rose-600 via-red-600 to-rose-700 hover:from-rose-500 hover:via-red-500 hover:to-rose-600 text-white font-black text-sm tracking-wider uppercase shadow-[0_0_25px_rgba(225,29,72,0.35)] hover:shadow-[0_0_35px_rgba(225,29,72,0.5)] transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <PauseIcon />
                  <span>{actionLoading ? "ACTIVANDO STALL..." : `ACTIVAR STALL DE PRECIO (${durationMinutes} MIN)`}</span>
                </button>
              )}
            </div>
          </div>

        </div>

        {/* ========================================================================= */}
        {/* AUDIT LOG & INCIDENT HISTORY TABLE                                        */}
        {/* ========================================================================= */}
        <div className="rounded-2xl bg-[#111827]/80 border border-slate-800 p-6 shadow-xl backdrop-blur-md">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-slate-800/80 text-cyan-400 border border-slate-700/60">
                <HistoryIcon sx={{ fontSize: 20 }} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white tracking-tight">Historial de Auditoría de Stalls</h2>
                <p className="text-xs text-slate-400">Registro inmutable de intervenciones manuales en OneZeroConn</p>
              </div>
            </div>
            <span className="text-xs font-mono text-slate-500">25 registros más recientes</span>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-800/90">
            <table className="w-full text-left text-sm text-slate-300">
              <thead className="bg-slate-900/90 text-[11px] font-mono tracking-wider text-slate-400 uppercase border-b border-slate-800">
                <tr>
                  <th className="py-3.5 px-4"># ID</th>
                  <th className="py-3.5 px-4">FECHA SOLICITUD</th>
                  <th className="py-3.5 px-4">OPERADOR</th>
                  <th className="py-3.5 px-4">DURACIÓN</th>
                  <th className="py-3.5 px-4">JUSTIFICACIÓN</th>
                  <th className="py-3.5 px-4">ESTADO</th>
                  <th className="py-3.5 px-4">FINALIZADO / CANCELADO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 font-sans">
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-500 font-mono text-xs">
                      No hay registros de stall en el historial.
                    </td>
                  </tr>
                ) : (
                  history.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-800/40 transition-colors">
                      <td className="py-3 px-4 font-mono font-bold text-slate-400 text-xs">
                        #{log.id.toString().padStart(3, "0")}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-300">
                        {formatDate(log.requested_at)}
                      </td>
                      <td className="py-3 px-4 text-xs font-semibold text-white">
                        {log.requested_by}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-cyan-400">
                        {log.duration_minutes} min
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-300 max-w-xs truncate" title={log.reason}>
                        {log.reason}
                      </td>
                      <td className="py-3 px-4 text-xs">
                        {log.status === "active" ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono font-black uppercase bg-rose-500/10 text-rose-400 border border-rose-500/30">
                            <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
                            ACTIVO
                          </span>
                        ) : log.status === "cancelled" ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono font-black uppercase bg-amber-500/10 text-amber-400 border border-amber-500/30">
                            CANCELADO
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono font-semibold uppercase bg-slate-800 text-slate-400 border border-slate-700">
                            {log.status.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-400">
                        {log.cancelled_at ? formatDate(log.cancelled_at) : log.expired_at ? formatDate(log.expired_at) : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default TickControl;
